import React, { useEffect, useState } from "react";
import axios from "axios";
import { useMessageModal } from "../admin/contexts/MessageModalContext";
import "../styles/bookDemoModal.css";

interface BookDemoModalProps {
  isOpen: boolean;
  onClose: () => void;
  software?: {
    id: number;
    title: string;
  } | null;
}

interface SubscriberInfo {
  id?: number;
  name?: string;
  email?: string;
  image?: string | null;
}

const BASE = import.meta.env.VITE_LYRAZONE_BASE_URL;
const IMAGE_URL = import.meta.env.VITE_LYRAZONE_IMAGE_URL;

const BookDemoModal: React.FC<BookDemoModalProps> = ({
  isOpen,
  onClose,
  software,
}) => {
  const { showModal } = useMessageModal();

  const [subscriber, setSubscriber] = useState<SubscriberInfo | null>(null);
  const [userImage, setUserImage] = useState<string | null>(null);

  const [demoType, setDemoType] = useState<"online" | "onsite">("online");
  const [bookingDate, setBookingDate] = useState("");
  const [bookingTime, setBookingTime] = useState("");
  const [loading, setLoading] = useState(false);

  const getSubscriberFromLocalStorage = () => {
    const storedUser = localStorage.getItem("subscriber_user");

    if (!storedUser) return null;

    try {
      return JSON.parse(storedUser);
    } catch (error) {
      console.error("Invalid subscriber_user in localStorage:", error);
      return null;
    }
  };

  const resetForm = () => {
    setDemoType("online");
    setBookingDate("");
    setBookingTime("");
  };

  useEffect(() => {
    if (!isOpen) return;

    const localSubscriber = getSubscriberFromLocalStorage();

    if (!localSubscriber) {
      showModal({
        type: "error",
        title: "Error",
        message: "You are not logged in, please login first.",
      });

      onClose();
      return;
    }

    setSubscriber({
      id: localSubscriber.id,
      name: localSubscriber.name,
      email: localSubscriber.email,
      image: localSubscriber.image || null,
    });

    const fetchUserImage = async () => {
      try {
        const res = await axios.get(`${BASE}/userdata`, {
          withCredentials: true,
        });

        const user = res.data?.userData?.[0];

        if (user) {
          setSubscriber((prev) => ({
            ...prev,
            id: user.id || prev?.id || localSubscriber.id,
            name: user.name || prev?.name || localSubscriber.name,
            email: user.email || prev?.email || localSubscriber.email,
            image: user.image || prev?.image || localSubscriber.image || null,
          }));

          setUserImage(user.image || null);
        }
      } catch (error) {
        console.error("Failed to fetch user image", error);
      }
    };

    fetchUserImage();
  }, [isOpen]);

  if (!isOpen) return null;

  const getSubscriberImage = () => {
    const image = userImage || subscriber?.image;

    if (!image) {
      return "/src/assets/img/user/default-user.png";
    }

    if (image.startsWith("http")) {
      return image;
    }

    return `${IMAGE_URL}/uploads/subscribers/${image}`;
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleSubmit = async () => {
    const storedUser = getSubscriberFromLocalStorage();

    if (!storedUser || !subscriber?.id || !subscriber?.email) {
      showModal({
        type: "error",
        title: "Error",
        message: "You are not logged in, please login first.",
      });
      return;
    }

    if (!bookingDate) {
      showModal({
        type: "error",
        title: "Error",
        message: "Please select booking date.",
      });
      return;
    }

    if (!bookingTime) {
      showModal({
        type: "error",
        title: "Error",
        message: "Please select booking time.",
      });
      return;
    }

    try {
      setLoading(true);

      const payload = {
        subscriber_id: subscriber.id,
        subscriber_name: subscriber.name || "",
        subscriber_email: subscriber.email || "",
        subscriber_image: userImage || subscriber.image || "",

        software_id: software?.id || null,
        software_title: software?.title || "",

        demo_type: demoType,
        booking_date: bookingDate,
        booking_time: bookingTime,
      };

      const res = await axios.post(`${BASE}/admin/book-demo/website`, payload, {
        withCredentials: true,
      });

      if (res.data.success) {
        showModal({
          type: "success",
          title: "Success",
          message: "Your demo booking has been submitted successfully.",
          confirmText: "OK",
        });

        resetForm();
        onClose();
      } else {
        showModal({
          type: "error",
          title: "Error",
          message: res.data.message || "Unable to submit demo booking.",
        });
      }
    } catch (error: any) {
      console.error("Book Demo Error:", error);

      showModal({
        type: "error",
        title: "Error",
        message:
          error?.response?.data?.message ||
          "Something went wrong while booking demo.",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="book-demo-overlay">
      <div className="book-demo-modal">
        <button className="book-demo-close" onClick={handleClose}>
          ×
        </button>

        <div className="book-demo-header">
          <h2>Book a Demo</h2>
          {software?.title && <p>{software.title}</p>}
        </div>

        <div className="book-demo-user-card">
          <img
            src={getSubscriberImage()}
            alt={subscriber?.name || "Subscriber"}
            className="book-demo-user-img"
          />

          <div>
            <h4>{subscriber?.name || "Guest User"}</h4>
            <p>{subscriber?.email || "Please login to book a demo"}</p>
          </div>
        </div>

        <div className="book-demo-field">
          <label>Demo Type</label>

          <div className="book-demo-radio-group">
            <label
              className={`book-demo-radio ${
                demoType === "online" ? "active" : ""
              }`}
            >
              <input
                type="radio"
                name="demoType"
                value="online"
                checked={demoType === "online"}
                onChange={() => setDemoType("online")}
              />

              <span className="radio-icon">💻</span>
              <span>Online</span>
            </label>

            <label
              className={`book-demo-radio ${
                demoType === "onsite" ? "active" : ""
              }`}
            >
              <input
                type="radio"
                name="demoType"
                value="onsite"
                checked={demoType === "onsite"}
                onChange={() => setDemoType("onsite")}
              />

              <span className="radio-icon">🏢</span>
              <span>On Site</span>
            </label>
          </div>
        </div>

        <div className="book-demo-grid">
          <div className="book-demo-field">
            <label>Date</label>
            <input
              type="date"
              value={bookingDate}
              min={new Date().toISOString().split("T")[0]}
              onChange={(e) => setBookingDate(e.target.value)}
            />
          </div>

          <div className="book-demo-field">
            <label>Time</label>
            <input
              type="time"
              value={bookingTime}
              onChange={(e) => setBookingTime(e.target.value)}
            />
          </div>
        </div>

        <button
          className="book-demo-submit"
          onClick={handleSubmit}
          disabled={loading}
        >
          {loading ? "Booking..." : "Book Now"}
        </button>
      </div>
    </div>
  );
};

export default BookDemoModal;
